import React, { useState } from "react";
import { UserSettings } from "../types";
import { 
  Settings, 
  UserCheck, 
  Flame, 
  Database, 
  HelpCircle, 
  Shield,
  Lock,
  ShieldCheck,
  Layers
} from "lucide-react";

interface SettingsPanelProps {
  settings: UserSettings;
  onSettingsChange: (next: UserSettings) => void;
}

export default function SettingsPanel({ settings, onSettingsChange }: SettingsPanelProps) {
  const [showSandboxToken, setShowSandboxToken] = useState(false);

  const updateSetting = <K extends keyof UserSettings>(key: K, value: UserSettings[K]) => {
    onSettingsChange({
      ...settings,
      [key]: value
    });
  };

  return (
    <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-6 shadow-xl" id="engine-settings-panel">
      <div className="flex items-center gap-3 border-b border-neutral-800 pb-4 mb-6">
        <div className="p-2.5 rounded-xl bg-amber-500/10 text-amber-500">
          <Settings className="w-5 h-5" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-neutral-100">Engine Settings</h3>
          <p className="text-xs text-neutral-500 font-sans">Calibrate Ancient Runes phonetic algorithms and preferences</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Pitch Calibration */}
        <div className="flex flex-col gap-4">
          <div>
            <h4 className="text-sm font-semibold text-neutral-200 flex items-center gap-1.5 mb-1">
              <UserCheck className="w-4 h-4 text-amber-500" /> Vocal Pitch Profile
            </h4>
            <p className="text-[11px] text-neutral-500">
              Select your natural vocal range for precise pitch detection and calibration.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-2">
            {[
              { id: "low-male", label: "Low (Bass)", range: "80 - 160 Hz" },
              { id: "medium-male", label: "Medium (Baritone/Tenor)", range: "130 - 260 Hz" },
              { id: "high-female", label: "High (Alto/Soprano)", range: "200 - 450 Hz" }
            ].map((prof) => (
              <button
                key={prof.id}
                onClick={() => updateSetting("pitchProfile", prof.id as any)}
                className={`flex flex-col items-center justify-center p-3 rounded-xl border text-center transition-all cursor-pointer ${
                  settings.pitchProfile === prof.id
                    ? "bg-amber-500/10 border-amber-500 text-amber-400 font-medium"
                    : "bg-neutral-950/40 border-neutral-800/80 text-neutral-400 hover:border-neutral-700"
                }`}
              >
                <span className="text-xs">{prof.label}</span>
                <span className="text-[10px] text-neutral-500 font-mono mt-0.5">{prof.range}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Hawan Step Preferences */}
        <div className="flex flex-col gap-4">
          <div>
            <h4 className="text-sm font-semibold text-neutral-200 flex items-center gap-1.5 mb-1">
              <Flame className="w-4 h-4 text-amber-500" /> Ritual Preferences
            </h4>
            <p className="text-[11px] text-neutral-500">
              Customize the step-by-step guidance and audio synchronization during Hawan.
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2.5 text-xs text-neutral-300 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={settings.autoScroll}
                onChange={(e) => updateSetting("autoScroll", e.target.checked)}
                className="rounded border-neutral-800 text-amber-500 focus:ring-0 focus:ring-offset-0 bg-neutral-900"
              />
              Auto-scroll active ritual steps
            </label>
            <p className="text-[10px] text-neutral-500 pl-6">
              Keeps current mantra instructions centered as recitation advances.
            </p>
          </div>
        </div>

        {/* Display Formatting */}
        <div className="flex flex-col gap-4">
          <div>
            <h4 className="text-sm font-semibold text-neutral-200 flex items-center gap-1.5 mb-1">
              <Layers className="w-4 h-4 text-amber-500" /> Script Display
            </h4>
            <p className="text-[11px] text-neutral-500">
              Toggle original Devanagari/Runic scripts and phonetic transliteration.
            </p>
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-xs text-neutral-300 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={settings.showDevanagari}
                onChange={(e) => updateSetting("showDevanagari", e.target.checked)}
                className="rounded border-neutral-800 text-amber-500 focus:ring-0 focus:ring-offset-0 bg-neutral-900"
              />
              Original Script (अग्नि / Runes)
            </label>

            <label className="flex items-center gap-2 text-xs text-neutral-300 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={settings.showTransliteration}
                onChange={(e) => updateSetting("showTransliteration", e.target.checked)}
                className="rounded border-neutral-800 text-amber-500 focus:ring-0 focus:ring-offset-0 bg-neutral-900"
              />
              IAST Transliteration (agni)
            </label>
          </div>
        </div>

        {/* Database Persistence Info */}
        <div className="flex flex-col gap-4">
          <div>
            <h4 className="text-sm font-semibold text-neutral-200 flex items-center gap-1.5 mb-1">
              <Database className="w-4 h-4 text-amber-500" /> Cloudflare D1 & Storage
            </h4>
            <p className="text-[11px] text-neutral-500">
              User progress and mantra histories are persisted safely.
            </p>
          </div>

          <div className="bg-neutral-950/40 p-4 rounded-xl border border-neutral-800/80 flex items-start gap-3">
            <HelpCircle className="w-4 h-4 text-neutral-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1 text-[11px] leading-relaxed text-neutral-400">
              <p className="font-semibold text-neutral-300 mb-0.5">Offline-First Local Storage Active</p>
              By default, all your mantras, ritual history, and practice sessions are saved directly in your browser. This enables completely offline usage of the Ancient Runes phonetic engine!
            </div>
          </div>
        </div>

        {/* Cryptographic Security & Token Authentication Panel */}
        <div className="border-t border-neutral-800 pt-6 mt-2 md:col-span-2">
          <h4 className="text-sm font-semibold text-neutral-100 flex items-center gap-2 mb-3">
            <Shield className="w-4 h-4 text-emerald-500" /> Active Security Audit & Cloudflare D1 Protection
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            
            {/* Web Crypto Zero-Knowledge Encryption */}
            <div className="bg-neutral-950 p-4 rounded-xl border border-neutral-800/80 flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-emerald-400" />
                <span className="text-xs font-semibold text-neutral-200">Zero-Knowledge Web Crypto</span>
              </div>
              <p className="text-[11px] text-neutral-400 leading-relaxed font-sans">
                Local configurations and practice journal notes are encrypted on-the-fly using browser-level <span className="text-amber-500 font-mono">AES-GCM (256-bit)</span> and signed with custom bitwise hashing before being synchronized to Cloudflare D1. The cloud database only contains ciphertext.
              </p>
              <div className="mt-auto pt-2">
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[9px] font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-mono">
                  ● Local AES-256-GCM Active
                </span>
              </div>
            </div>

            {/* Dynamic Client Token Authentication (JWT) */}
            <div className="bg-neutral-950 p-4 rounded-xl border border-neutral-800/80 flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-emerald-400" />
                <span className="text-xs font-semibold text-neutral-200">Client Token Verification</span>
              </div>
              <p className="text-[11px] text-neutral-400 leading-relaxed font-sans">
                All write and read operations to the server D1 SQL storage are locked behind JWT Authorization headers. The backend page handlers validate the incoming token or secure Developer Sandbox token to block unauthorized access.
              </p>
              <div className="mt-auto pt-2 flex items-center justify-between gap-2">
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[9px] font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-mono">
                  ● JWT Middleware Enforced
                </span>
                <button 
                  onClick={() => setShowSandboxToken(!showSandboxToken)} 
                  className="text-[9px] text-neutral-500 hover:text-neutral-300 font-mono underline cursor-pointer"
                >
                  {showSandboxToken ? "Hide Key" : "View Key"}
                </button>
              </div>
              {showSandboxToken && (
                <div className="bg-neutral-900 border border-neutral-800 rounded p-1.5 mt-2 font-mono text-[9px] text-neutral-400 select-all break-all">
                  Bearer VedicDevSandboxToken_2026
                </div>
              )}
            </div>

          </div>
        </div>

      </div>
    </div>
  );
}
